import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import {
  auditLogCollection,
  billingCyclesCollection,
  businessesCollection,
  paymentMethodConfigsCollection,
  subscriptionPaymentsCollection,
  subscriptionPlansCollection,
  subscriptionDiscountsCollection,
  discountCodesCollection,
  syncApiUrl,
  getAuthToken,
  planOwnCyclePrices,
  type AuditLog,
  type Business,
  type BillingCycle,
  type PaymentMethodConfig,
  type SubscriptionPlan,
  type SubscriptionDiscount,
  type DiscountCode,
} from "@cliqsuite/db";
import { capitalize, fmtDate, mmk, nowIso } from "../lib/format";
import type { BillingCycleView, Payment, Plan } from "../lib/adminTypes";
import { useAdminAuth } from "./AdminAuthContext";

export type CfgTab = "qr" | "bank" | "wallet";

/** The subset of platform.payment_method_configs a create/edit form submits. */
export type ConfigFormValue = Pick<
  PaymentMethodConfig,
  "name" | "instructions" | "bank_name" | "account_name" | "account_number" | "branch" | "network" | "wallet_address" | "image_data" | "is_enabled"
>;

export interface PlanFormValue {
  name: string;
  price: number;
  cyclePrices: Record<string, number>;
  stores: number;
  seats: number;
  blurb: string;
  mods: string[];
}

export interface SubscriptionFormValue {
  status: Business["subscription_status"];
  period_end: string | null;
  grace_period_ends_at: string | null;
  suspension_reason: string | null;
}

export interface DiscountFormValue {
  scope: "global" | "business";
  business_id: string | null;
  plan_id: string | null;
  discount_type: "percent" | "flat";
  value: number;
  label: string;
  is_active: boolean;
  expires_at: string | null;
}

export interface DiscountCodeFormValue {
  code: string;
  plan_id: string | null;
  discount_type: "percent" | "flat";
  value: number;
  max_redemptions: number | null;
  label: string;
  is_active: boolean;
  expires_at: string | null;
}

interface AdminDataValue {
  payments: Payment[];
  plans: Plan[];
  cycles: BillingCycleView[];
  paycfg: Record<CfgTab, PaymentMethodConfig[]>;
  auditLog: AuditLog[];
  planSubscriberCounts: Record<string, number>;
  toast: string | null;
  flash: (msg: string) => void;

  // verification actions
  approve: (id: string) => Promise<void>;
  reject: (id: string, reason: string) => void;
  reopen: (id: string) => void;

  // payment method config actions
  cfgToggle: (tab: CfgTab, id: string) => void;
  cfgDelete: (tab: CfgTab, id: string) => void;
  cfgSave: (tab: CfgTab, id: string | null, item: ConfigFormValue) => void;

  // plan actions
  planSave: (key: string | null, form: PlanFormValue) => void;
  planArchive: (key: string, archived: boolean) => void;
  cycleSave: (id: string, form: { label: string; months: number; multiplier: number }) => void;

  // move a business onto a different plan — writes core.businesses.subscription_plan_id directly
  businessReassign: (businessId: string, planKey: string) => void;

  // the missing CRUD: reassigning a plan never touched status/period_end/
  // grace_period_ends_at (only the approve-payment flow did) — this writes
  // all three directly, for correcting/extending a subscription by hand.
  businessSetSubscription: (businessId: string, form: SubscriptionFormValue) => void;

  // irreversible: deletes the business and every row scoped to it (see
  // services/sync-api/src/routes/core.ts's POST /core/businesses/:id/wipe)
  wipeBusiness: (businessId: string, confirmName: string) => Promise<{ ok: boolean; error?: string }>;

  // standing subscription discounts — global or per-business, optionally plan-specific
  discounts: SubscriptionDiscount[];
  discountSave: (id: string | null, form: DiscountFormValue) => void;
  discountDelete: (id: string) => void;
  discountToggle: (id: string, active: boolean) => void;

  // redeemable discount codes — owners enter these at payment time
  discountCodes: DiscountCode[];
  codeSave: (id: string | null, form: DiscountCodeFormValue) => void;
  codeDelete: (id: string) => void;
  codeToggle: (id: string, active: boolean) => void;
}

const AdminDataContext = createContext<AdminDataValue | null>(null);

export const cyclePriceOf = (plan: Pick<Plan, "price" | "cyclePrices">, cycle: Pick<BillingCycleView, "key" | "mult">) => plan.cyclePrices[cycle.key] ?? Math.round(plan.price * cycle.mult);

function planFeatures(row: SubscriptionPlan): { modules: string[]; blurb: string; custom: boolean } {
  const f = (row.features ?? {}) as { modules?: string[]; blurb?: string; custom?: boolean };
  return { modules: f.modules ?? [], blurb: f.blurb ?? "", custom: !!f.custom };
}

function toPlanView(row: SubscriptionPlan): Plan {
  const f = planFeatures(row);
  return {
    id: row.id,
    key: row.key,
    name: row.name,
    blurb: f.blurb,
    price: row.base_price,
    cyclePrices: planOwnCyclePrices(row),
    stores: row.max_stores,
    seats: row.max_staff,
    mods: f.modules,
    custom: f.custom,
    archived: !row.is_active,
    sortOrder: row.sort_order,
  };
}

export function AdminDataProvider({ children }: { children: ReactNode }) {
  const { user } = useAdminAuth();
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }, []);

  const { data: paymentRows } = useLiveQuery((q) => q.from({ p: subscriptionPaymentsCollection }));
  const { data: planRows } = useLiveQuery((q) => q.from({ p: subscriptionPlansCollection }));
  const { data: cycleRows } = useLiveQuery((q) => q.from({ c: billingCyclesCollection }));
  const { data: businessRows } = useLiveQuery((q) => q.from({ b: businessesCollection }));
  const { data: discountRows } = useLiveQuery((q) => q.from({ d: subscriptionDiscountsCollection }));
  const { data: codeRows } = useLiveQuery((q) => q.from({ c: discountCodesCollection }));
  const { data: cfgRows } = useLiveQuery((q) => q.from({ c: paymentMethodConfigsCollection }));
  const { data: auditRows } = useLiveQuery((q) => q.from({ a: auditLogCollection }).orderBy(({ a }) => a.created_at, "desc").limit(300));

  const insertAudit = useCallback(
    (entry: { action: string; payment_ref?: string | null; detail?: string | null; metadata?: Record<string, unknown> | null; system?: boolean }) => {
      const isSystem = !!entry.system;
      auditLogCollection.insert({
        id: crypto.randomUUID(),
        action: entry.action,
        actor_type: isSystem ? "system" : "admin",
        actor_id: isSystem ? null : (user?.id ?? null),
        actor_name: isSystem ? "System" : (user?.name || user?.email || "Admin"),
        actor_role: isSystem ? "System" : capitalize(user?.role || "admin"),
        payment_ref: entry.payment_ref ?? null,
        detail: entry.detail ?? null,
        metadata: entry.metadata ?? null,
        created_at: nowIso(),
      });
    },
    [user],
  );

  const businessById = useMemo(() => {
    const m = new Map<string, Business>();
    (businessRows ?? []).forEach((b) => m.set(b.id, b));
    return m;
  }, [businessRows]);

  const planById = useMemo(() => {
    const m = new Map<string, SubscriptionPlan>();
    (planRows ?? []).forEach((p) => m.set(p.id, p));
    return m;
  }, [planRows]);

  const cycleById = useMemo(() => {
    const m = new Map<string, BillingCycle>();
    (cycleRows ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [cycleRows]);

  const cycles = useMemo<BillingCycleView[]>(() => {
    const rows = (cycleRows ?? []).map((c) => ({ id: c.id, key: c.key, label: c.label, months: c.months, mult: Number(c.multiplier) || 1 }));
    rows.sort((a, b) => a.months - b.months);
    return rows;
  }, [cycleRows]);

  const plans = useMemo<Plan[]>(() => (planRows ?? []).map(toPlanView).sort((a, b) => a.sortOrder - b.sortOrder), [planRows]);

  const discounts = useMemo(
    () => [...(discountRows ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [discountRows],
  );
  const discountCodes = useMemo(
    () => [...(codeRows ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [codeRows],
  );

  // "reviewed by" text for a payment is deliberately looked up from the
  // (immutable) audit log rather than resolved from reviewed_by_admin_id —
  // platform.admin_users has no client-facing collection, but the audit
  // trail already denormalizes actor_name/actor_role at the moment of the
  // approve/reject action, which is exactly what the drawer wants to show.
  const reviewActorByRef = useMemo(() => {
    const m = new Map<string, string>();
    (auditRows ?? []).forEach((a) => {
      if (!a.payment_ref || m.has(a.payment_ref)) return;
      if (a.action !== "Payment approved" && a.action !== "Payment rejected") return;
      m.set(a.payment_ref, a.actor_name ? `${a.actor_name}${a.actor_role ? ` (${a.actor_role})` : ""}` : "Admin");
    });
    return m;
  }, [auditRows]);

  const payments = useMemo<Payment[]>(() => {
    return (paymentRows ?? []).map((p) => {
      const biz = businessById.get(p.business_id);
      const plan = planById.get(p.plan_id);
      const cycle = cycleById.get(p.billing_cycle_id);
      return {
        id: p.id,
        ref: p.reference,
        businessId: p.business_id,
        business: biz?.name ?? "Unknown business",
        businessType: biz ? capitalize(biz.business_type) : "",
        billingEmail: biz?.billing_email ?? "",
        planKey: plan?.key ?? "",
        planName: plan?.name ?? "—",
        cycleKey: cycle?.key ?? "",
        cycleLabel: cycle?.label ?? "",
        cycleMonths: cycle?.months ?? 1,
        amount: p.amount,
        method: p.payment_method_type,
        methodLabel: p.payment_method_name,
        txid: p.txid ?? "",
        notes: p.notes ?? "",
        proof: p.proof_data,
        status: p.status,
        dateLabel: fmtDate(p.submitted_at),
        submittedAt: p.submitted_at,
        reviewedAt: p.reviewed_at,
        reviewedBy: reviewActorByRef.get(p.reference) ?? null,
        rejectReason: p.reject_reason ?? "",
        periodEnd: p.period_end ? fmtDate(p.period_end) : "",
        periodEndIso: p.period_end,
        resubmitted: p.resubmission_count > 0,
      };
    });
  }, [paymentRows, businessById, planById, cycleById, reviewActorByRef]);

  const paycfg = useMemo<Record<CfgTab, PaymentMethodConfig[]>>(() => {
    const rows = (cfgRows ?? []).filter((r) => !r.deleted_at).slice().sort((a, b) => a.sort_order - b.sort_order);
    return {
      qr: rows.filter((r) => r.type === "qr"),
      bank: rows.filter((r) => r.type === "bank"),
      wallet: rows.filter((r) => r.type === "wallet"),
    };
  }, [cfgRows]);

  const planSubscriberCounts = useMemo(() => {
    const byPlanId = new Map<string, number>();
    (businessRows ?? []).forEach((b) => {
      if (!b.subscription_plan_id) return;
      byPlanId.set(b.subscription_plan_id, (byPlanId.get(b.subscription_plan_id) ?? 0) + 1);
    });
    const out: Record<string, number> = {};
    (planRows ?? []).forEach((p) => {
      out[p.key] = byPlanId.get(p.id) ?? 0;
    });
    return out;
  }, [businessRows, planRows]);

  // ===== verification actions =====
  // Approving a payment also activates the business's subscription — both
  // writes must happen in one transaction, so this goes through a
  // dedicated sync-api endpoint (services/sync-api/src/routes/platform.ts,
  // POST /platform/subscription-payments/:id/approve) instead of two
  // separate collection.update() calls with no atomicity between them.
  const approve = useCallback(
    async (id: string) => {
      const raw = (paymentRows ?? []).find((p) => p.id === id);
      if (!raw || raw.status === "approved") return;

      try {
        const res = await fetch(syncApiUrl(`/platform/subscription-payments/${id}/approve`), {
          method: "POST",
          headers: {
            "content-type": "application/json",
            ...(getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {}),
          },
        });
        if (!res.ok) {
          flash(res.status === 409 ? "Already reviewed — refresh and try again" : "Couldn't approve — try again");
          return;
        }
        const { business } = (await res.json()) as { business: { subscription_period_end: string } };

        const biz = businessById.get(raw.business_id);
        const plan = planById.get(raw.plan_id);
        insertAudit({ action: "Payment approved", payment_ref: raw.reference, detail: `${biz?.name ?? "Business"} · ${plan?.name ?? "Plan"} · ${mmk(raw.amount)}` });
        insertAudit({
          action: "Subscription activated",
          payment_ref: raw.reference,
          detail: `${biz?.name ?? "Business"} · active until ${fmtDate(business.subscription_period_end)}`,
          system: true,
        });
        flash("Approved — subscription activated");
      } catch {
        flash("Couldn't approve — try again");
      }
    },
    [paymentRows, businessById, planById, insertAudit, flash],
  );

  const reject = useCallback(
    (id: string, reason: string) => {
      if (!reason.trim()) {
        flash("Enter a rejection reason");
        return;
      }
      const raw = (paymentRows ?? []).find((p) => p.id === id);
      subscriptionPaymentsCollection.update(id, (draft) => {
        draft.status = "rejected";
        draft.reviewed_by_admin_id = user?.id ?? null;
        draft.reviewed_at = nowIso();
        draft.reject_reason = reason.trim();
      });
      if (raw) insertAudit({ action: "Payment rejected", payment_ref: raw.reference, detail: reason.trim() });
      const who = raw ? businessById.get(raw.business_id)?.name : undefined;
      flash(who ? `Rejected. ${who} can re-submit` : "Payment rejected");
    },
    [paymentRows, user, businessById, insertAudit, flash],
  );

  const reopen = useCallback(
    (id: string) => {
      const raw = (paymentRows ?? []).find((p) => p.id === id);
      if (!raw || raw.status !== "rejected") return;
      subscriptionPaymentsCollection.update(id, (draft) => {
        draft.status = "pending";
        draft.resubmission_count = Math.min(3, (draft.resubmission_count ?? 0) + 1);
        draft.reviewed_at = null;
        draft.reviewed_by_admin_id = null;
        draft.reject_reason = null;
      });
      const biz = businessById.get(raw.business_id);
      insertAudit({ action: "Reopened for re-submission", payment_ref: raw.reference, detail: `${biz?.name ?? "Business"} · merchant asked to re-submit proof` });
      flash(`${raw.reference} reopened — merchant can re-submit`);
    },
    [paymentRows, businessById, insertAudit, flash],
  );

  // ===== payment method config actions =====
  const cfgToggle = useCallback(
    (_tab: CfgTab, id: string) => {
      const row = (cfgRows ?? []).find((r) => r.id === id);
      if (!row) return;
      const nextEnabled = !row.is_enabled;
      paymentMethodConfigsCollection.update(id, (draft) => {
        draft.is_enabled = nextEnabled;
      });
      insertAudit({ action: "Configuration changed", detail: `${nextEnabled ? "Enabled " : "Disabled "}${row.name}` });
      flash("Updated");
    },
    [cfgRows, insertAudit, flash],
  );

  const cfgDelete = useCallback(
    (_tab: CfgTab, id: string) => {
      const row = (cfgRows ?? []).find((r) => r.id === id);
      paymentMethodConfigsCollection.delete(id);
      insertAudit({ action: "Configuration changed", detail: `Removed ${row?.name ?? "method"}` });
      flash("Removed");
    },
    [cfgRows, insertAudit, flash],
  );

  const cfgSave = useCallback(
    (tab: CfgTab, id: string | null, item: ConfigFormValue) => {
      if (id) {
        paymentMethodConfigsCollection.update(id, (draft) => {
          draft.name = item.name;
          draft.instructions = item.instructions;
          draft.bank_name = item.bank_name;
          draft.account_name = item.account_name;
          draft.account_number = item.account_number;
          draft.branch = item.branch;
          draft.network = item.network;
          draft.wallet_address = item.wallet_address;
          draft.image_data = item.image_data;
          draft.is_enabled = item.is_enabled;
        });
        insertAudit({ action: "Configuration changed", detail: `Updated ${item.name}` });
        flash(`${item.name} saved`);
      } else {
        const maxSort = (cfgRows ?? []).reduce((m, r) => Math.max(m, r.sort_order), 0);
        const ts = nowIso();
        paymentMethodConfigsCollection.insert({
          id: crypto.randomUUID(),
          type: tab,
          name: item.name,
          instructions: item.instructions,
          bank_name: item.bank_name,
          account_name: item.account_name,
          account_number: item.account_number,
          branch: item.branch,
          network: item.network,
          wallet_address: item.wallet_address,
          image_data: item.image_data,
          is_enabled: item.is_enabled,
          sort_order: maxSort + 1,
          created_at: ts,
          updated_at: ts,
          deleted_at: null,
        });
        insertAudit({ action: "Configuration changed", detail: `Added ${item.name}` });
        flash("Added");
      }
    },
    [cfgRows, insertAudit, flash],
  );

  // ===== plan actions =====
  const planSave = useCallback(
    (key: string | null, form: PlanFormValue) => {
      const name = form.name.trim();
      const price = Math.max(0, Math.round(form.price || 0));
      const stores = Math.max(1, Math.round(form.stores || 1));
      const seats = Math.max(1, Math.round(form.seats || 1));
      const ts = nowIso();

      if (!key) {
        if (!name) {
          flash("Enter a plan name");
          return;
        }
        const existingKeys = new Set((planRows ?? []).map((p) => p.key));
        const base = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "plan";
        let newKey = base;
        let n = 1;
        while (existingKeys.has(newKey)) newKey = `${base}-${++n}`;
        const maxSort = (planRows ?? []).reduce((m, p) => Math.max(m, p.sort_order), 0);
        subscriptionPlansCollection.insert({
          id: crypto.randomUUID(),
          key: newKey,
          name,
          base_price: price,
          max_stores: stores,
          max_staff: seats,
          max_products: 100,
          features: { blurb: form.blurb, modules: form.mods, custom: true, cycle_prices: form.cyclePrices },
          is_active: true,
          sort_order: maxSort + 1,
          created_at: ts,
          updated_at: ts,
        });
        insertAudit({ action: "Plan updated", detail: `Created ${name} · ${mmk(price)}/mo` });
        flash("Plan created");
      } else {
        const row = (planRows ?? []).find((p) => p.key === key);
        if (!row) return;
        subscriptionPlansCollection.update(row.id, (draft) => {
          draft.name = name || row.name;
          draft.base_price = price;
          draft.max_stores = stores;
          draft.max_staff = seats;
          draft.features = { ...planFeatures(row), blurb: form.blurb || planFeatures(row).blurb, modules: form.mods, cycle_prices: form.cyclePrices };
        });
        insertAudit({ action: "Plan updated", detail: `${name || row.name} · ${mmk(price)}/mo · ${form.mods.length} modules` });
        flash(`${name || row.name} saved`);
      }
    },
    [planRows, insertAudit, flash],
  );

  const planArchive = useCallback(
    (key: string, archived: boolean) => {
      const row = (planRows ?? []).find((p) => p.key === key);
      if (!row) return;
      subscriptionPlansCollection.update(row.id, (draft) => {
        draft.is_active = !archived;
      });
      insertAudit({ action: "Plan updated", detail: `${archived ? "Archived " : "Restored "}${row.name}` });
      flash(`${row.name} ${archived ? "archived" : "restored"}`);
    },
    [planRows, insertAudit, flash],
  );

  const cycleSave = useCallback(
    (id: string, form: { label: string; months: number; multiplier: number }) => {
      const label = form.label.trim();
      const months = Math.max(1, Math.round(form.months || 1));
      const multiplier = Math.max(0, Number(form.multiplier) || 0);
      if (!label) {
        flash("Enter a label");
        return;
      }
      billingCyclesCollection.update(id, (draft) => {
        draft.label = label;
        draft.months = months;
        draft.multiplier = multiplier.toFixed(2);
      });
      insertAudit({ action: "Billing cycle updated", detail: `${label} · ${months}mo · ×${multiplier.toFixed(2)}` });
      flash("Billing cycle saved");
    },
    [insertAudit, flash],
  );

  const businessReassign = useCallback(
    (businessId: string, planKey: string) => {
      const plan = (planRows ?? []).find((p) => p.key === planKey);
      const biz = businessById.get(businessId);
      if (!plan || !biz) return;
      if (biz.subscription_plan_id === plan.id) {
        flash(`${biz.name} is already on ${plan.name}`);
        return;
      }
      businessesCollection.update(businessId, (draft) => {
        draft.subscription_plan_id = plan.id;
      });
      insertAudit({ action: "Plan updated", detail: `${biz.name} → ${plan.name} (manual change)` });
      flash(`${biz.name} moved to ${plan.name}`);
    },
    [planRows, businessById, insertAudit, flash],
  );

  const businessSetSubscription = useCallback(
    (businessId: string, form: SubscriptionFormValue) => {
      const biz = businessById.get(businessId);
      if (!biz) return;
      businessesCollection.update(businessId, (draft) => {
        draft.subscription_status = form.status;
        draft.subscription_period_end = form.period_end;
        draft.grace_period_ends_at = form.grace_period_ends_at;
        draft.suspension_reason = form.suspension_reason;
      });
      insertAudit({
        action: "Subscription updated",
        detail: `${biz.name} · ${form.status}${form.period_end ? ` · until ${fmtDate(form.period_end)}` : " · no active period"} (manual change)`,
      });
      flash(`${biz.name} updated`);
    },
    [businessById, insertAudit, flash],
  );

  const discountSave = useCallback(
    (id: string | null, form: DiscountFormValue) => {
      const now = nowIso();
      const summary = `${form.value}${form.discount_type === "percent" ? "%" : " MMK"} off${form.label ? ` · ${form.label}` : ""}`;
      if (id) {
        subscriptionDiscountsCollection.update(id, (draft) => {
          draft.scope = form.scope;
          draft.business_id = form.business_id;
          draft.plan_id = form.plan_id;
          draft.discount_type = form.discount_type;
          draft.value = form.value;
          draft.label = form.label || null;
          draft.is_active = form.is_active;
          draft.expires_at = form.expires_at;
        });
        insertAudit({ action: "Discount updated", detail: summary });
        flash(`${form.label || "Discount"} updated`);
      } else {
        subscriptionDiscountsCollection.insert({
          id: crypto.randomUUID(),
          scope: form.scope,
          business_id: form.business_id,
          plan_id: form.plan_id,
          discount_type: form.discount_type,
          value: form.value,
          label: form.label || null,
          is_active: form.is_active,
          expires_at: form.expires_at,
          created_at: now,
          updated_at: now,
        });
        insertAudit({ action: "Discount created", detail: summary });
        flash(`${form.label || "Discount"} created`);
      }
    },
    [insertAudit, flash],
  );

  const discountDelete = useCallback(
    (id: string) => {
      const row = (discountRows ?? []).find((d) => d.id === id);
      subscriptionDiscountsCollection.delete(id);
      insertAudit({ action: "Discount removed", detail: row?.label ?? "Discount rule" });
      flash(`${row?.label ?? "Discount"} deleted`);
    },
    [discountRows, insertAudit, flash],
  );

  const discountToggle = useCallback((id: string, active: boolean) => {
    subscriptionDiscountsCollection.update(id, (draft) => {
      draft.is_active = active;
    });
  }, []);

  const codeSave = useCallback(
    (id: string | null, form: DiscountCodeFormValue) => {
      const now = nowIso();
      const code = form.code.trim().toUpperCase();
      if (id) {
        discountCodesCollection.update(id, (draft) => {
          draft.code = code;
          draft.plan_id = form.plan_id;
          draft.discount_type = form.discount_type;
          draft.value = form.value;
          draft.max_redemptions = form.max_redemptions;
          draft.label = form.label || null;
          draft.is_active = form.is_active;
          draft.expires_at = form.expires_at;
        });
        insertAudit({ action: "Discount code updated", detail: code });
        flash(`${code} updated`);
      } else {
        discountCodesCollection.insert({
          id: crypto.randomUUID(),
          code,
          plan_id: form.plan_id,
          discount_type: form.discount_type,
          value: form.value,
          max_redemptions: form.max_redemptions,
          redeemed_count: 0,
          label: form.label || null,
          is_active: form.is_active,
          expires_at: form.expires_at,
          created_at: now,
          updated_at: now,
        });
        insertAudit({ action: "Discount code created", detail: code });
        flash(`${code} created`);
      }
    },
    [insertAudit, flash],
  );

  const codeDelete = useCallback(
    (id: string) => {
      const row = (codeRows ?? []).find((c) => c.id === id);
      discountCodesCollection.delete(id);
      insertAudit({ action: "Discount code removed", detail: row?.code ?? "Code" });
      flash("Discount code removed");
    },
    [codeRows, insertAudit, flash],
  );

  const codeToggle = useCallback((id: string, active: boolean) => {
    discountCodesCollection.update(id, (draft) => {
      draft.is_active = active;
    });
  }, []);

  const wipeBusiness = useCallback(
    async (businessId: string, confirmName: string) => {
      try {
        const res = await fetch(syncApiUrl(`/core/businesses/${businessId}/wipe`), {
          method: "POST",
          headers: {
            "content-type": "application/json",
            ...(getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {}),
          },
          body: JSON.stringify({ confirmName }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          return { ok: false, error: body.error ?? `http_${res.status}` };
        }
        const data = (await res.json()) as { businessName: string; storeCount: number };
        insertAudit({
          action: "Business wiped",
          detail: `${data.businessName} · ${data.storeCount} store${data.storeCount === 1 ? "" : "s"} and all related data deleted`,
        });
        flash(`${data.businessName} deleted`);
        return { ok: true };
      } catch {
        return { ok: false, error: "network" };
      }
    },
    [insertAudit, flash],
  );

  const value = useMemo<AdminDataValue>(
    () => ({
      payments,
      plans,
      cycles,
      paycfg,
      auditLog: auditRows ?? [],
      planSubscriberCounts,
      toast,
      flash,
      approve,
      reject,
      reopen,
      cfgToggle,
      cfgDelete,
      cfgSave,
      planSave,
      planArchive,
      cycleSave,
      businessReassign,
      businessSetSubscription,
      wipeBusiness,
      discounts,
      discountSave,
      discountDelete,
      discountToggle,
      discountCodes,
      codeSave,
      codeDelete,
      codeToggle,
    }),
    [
      payments,
      plans,
      cycles,
      paycfg,
      auditRows,
      planSubscriberCounts,
      toast,
      flash,
      approve,
      reject,
      reopen,
      cfgToggle,
      cfgDelete,
      cfgSave,
      planSave,
      planArchive,
      cycleSave,
      businessReassign,
      businessSetSubscription,
      wipeBusiness,
      discounts,
      discountSave,
      discountDelete,
      discountToggle,
      discountCodes,
      codeSave,
      codeDelete,
      codeToggle,
    ],
  );

  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
}

export function useAdminData(): AdminDataValue {
  const ctx = useContext(AdminDataContext);
  if (!ctx) throw new Error("useAdminData must be used within AdminDataProvider");
  return ctx;
}
